import React, { useState, useEffect } from 'react';
import Fuse from 'fuse.js';
import {  onSetActiveGeography, useGlobalActiveGeography, onSetModuleId, onSetSTModuleId, onSetDBModuleId, onSetRGModuleId, useGlobalModuleId, onSetActiveModuleId, onSetActiveDBModuleId, onSetActiveRGModuleId, onSetActiveSTModuleId, onSetActiveIndicator, onSetSearchLatLng, useGlobalActivePage } from '../../data/StatusStore';
// 🔹 Import Stadia Maps client components from the package
import { GeocodingApi, AutocompleteRequest, Configuration } from '@stadiamaps/api';
import { active } from 'd3';
import { posthog } from '../utilities/posthog';
import { checkGeoAvailability } from '../utilities/Utilities';
import { siteConfig } from '../../config/siteConfig';

// 🔹 Synonyms Mapping (Bidirectional)
const synonymMap: { [key: string]: string[] } = {
  // "<user input>": ["<d2g synonym1>", "<d2g synonym2>", ...]
  // "male": ["female", "gender"],
  // "female": ["male", "gender"],
  // "sex": ["gender", "male", "female"],
  // "dementia": ["alzheimers"],
  // "memory loss": ["alzheimers"],
  // "memory": ["alzheimers"],
  // "ahdi": ["american human development index", "development"],
  // "earnings": ["income", "money"],
  // "elderly": ["old", "older"],
  // "youth": ["young", "kids", "children", "child"],
  // "education": ["school", "schooling"],
  // "income": ["earnings", "money"],
  // "employment": ["work", "working"],
  // "sector": ["industry", "occupation"],
  // "occupation": ["job", "industry"],
  // "poverty": ["poor"],
  // "rat": ["rodent"],
  "male": ["female", "gender"],
  "female": ["male", "gender"],
  "sex": ["gender", "male", "female"],
  "dementia": ["alzheimers"],
  "memory loss": ["alzheimers"],
  "memory": ["alzheimers"],
  "ahdi": ["american human development index", "development"],
  "earnings": ["income", "money"],
  "elderly": ["old", "older"],
  "youth": ["young", "kids", "children", "child"],
  "education": ["school", "schooling"],
  "income": ["earnings", "money", "wage", "salary"],
  "employment": ["work", "working"],
  "sector": ["industry", "occupation"],
  "occupation": ["job", "industry"],
  "poverty": ["poor"],
  "rat": ["rodent"],
  "pay": ["wage"],
  "paycheck": ["wage", "salary"],
  "apartment": ["housing"],
  "apartments": ["housing"],
  "transport": ["transit"],
  "public transport": ["transit"],
  "public transportation": ["transit"],
  "teacher": ["schools"],
  "teachers": ["schools"],
  "student": ["schools"],
  "students": ["schools"],
  "classroom": ["schools"],
  "campus": ["colleges", "universities"],
  "college": ["colleges", "universities"],
  "university": ["universities", "colleges"],
  "higher ed": ["colleges", "universities"],
  "immigration": ["foreign-born"],
  "immigrant": ["foreign-born"],
  "migrant": ["foreign-born"],
  "newcomer": ["foreign-born"],
  "job": ["employed"],
  "jobs": ["employed"],
  "work": ["employed"],
  "working": ["employed"],
  "workforce": ["employed"],
  "hiring": ["employed"],
  "military": ["veterans"],
  "army": ["veterans"],
  "navy": ["veterans"],
  "air force": ["veterans"],
  "service": ["veterans"],
  "projects": ["nycha"],
  "housing authority": ["nycha"],
  "public housing": ["nycha"],
  "section 8": ["housing"],
  "mortality": ["deaths"],
  "fatalities": ["deaths"],
  "birthrate": ["births"],
  "newborn": ["births"],
  "pregnancy": ["births"],
  "men": ["males"],
  "women": ["females"],
  "girl": ["females"],
  "boy": ["males"],
  "hardship": ["poverty"],
  "lowincome": ["poverty"],
  "poor": ["poverty"],
  "wellness": ["health"],
  "medical": ["health"],
  "sick": ["health"],
  "train": ["transit"],
  "metro": ["transit"],
  "subway": ["transit"],
};

// Disassociation Mapping (to prevent substring false positives)
// Each key can define:
// - required: regexes that must match (e.g., word boundary for 'rat'/'rats')
// - blocked: regexes that must NOT match (e.g., 'democrat'/'democrats')
const ratRules: { required?: RegExp[]; blocked?: RegExp[] } = {
  required: [/\brats?\b/i], // matches 'rat' or 'rats' with word boundaries
  blocked: [
    /democrats?/i,
    /aristocrats?/i,
    /bureaucrats?/i,
    /\brating\b/i,
    /\bratio\b/i,
  ],
};
const earnRules: { required?: RegExp[]; blocked?: RegExp[] } = {
  required: [/\bearn?\b/i], // matches 'earning' or 'earnings' with word boundaries
  blocked: [
    /hearing?/i,
  ],
};

const oldRules: { required?: RegExp[]; blocked?: RegExp[] } = {
  // Allow 'old' and 'older' as contiguous tokens; still block 'household(s)'.
  required: [/\bold(?:er)?\b/i],
  blocked: [
    /household?/i,
  ],
};

const dissociateMap: {
  [key: string]: { required?: RegExp[]; blocked?: RegExp[] }
} = {
  ra: ratRules,
  rat: ratRules,
  rats: ratRules,
  earn: earnRules,
  old: oldRules,
};

// Helper to apply dissociation rules to the final results
function applyDissociation(
  rawQuery: string,
  items: { key: string; data: string; lat?: number; lng?: number }[],
  enforceRequired: boolean = true,
  originMap?: Map<string, 'raw' | 'syn' | 'both' | 'geo'>
) {
  const words = rawQuery.toLowerCase().split(/\s+/).filter(Boolean);

  const activeRules = words
    .map((w) => dissociateMap[w])
    .filter(Boolean) as { required?: RegExp[]; blocked?: RegExp[] }[];

  if (activeRules.length === 0) return items;

  const matchesAny = (text: string, patterns?: RegExp[]) =>
    !!(patterns && patterns.some((re) => re.test(text)));

  return items.filter((item) => {
    const text = `${item.key} ${item.data}`;
    const origin = originMap?.get(item.key) || 'raw';
    // Only enforce required patterns for raw-origin (or both) items
    const shouldRequire = enforceRequired && (origin === 'raw' || origin === 'both');

    return activeRules.every(({ required, blocked }) => {
      const requiredOk = shouldRequire && required ? matchesAny(text, required) : true;
      const blockedOk = blocked ? !matchesAny(text, blocked) : true;
      return requiredOk && blockedOk;
    });
  });
}

// 🔹 Expands Search Query to Include Synonyms (Case-Insensitive)
const expandQuery = (query: string): string[] => {
  // Always lowercase the user input
  const words = query.toLowerCase().split(" ");
  const expandedQueries = new Set<string>();

  // ✅ Handle multi-word synonyms first (also check the map in lowercase)
  const multiWordKey = query.toLowerCase();
  if (synonymMap[multiWordKey]) {
    synonymMap[multiWordKey].forEach(syn => expandedQueries.add(syn.toLowerCase()));
  }

  // Helper to add synonyms for keys that start with a partial token (prefix expansion)
  const addPrefixSynonyms = (partial: string) => {
    const lowerPartial = partial.toLowerCase();
    if (lowerPartial.length < 3) return; // avoid noisy expansions
    Object.keys(synonymMap).forEach(k => {
      const lk = k.toLowerCase();
      if (lk.startsWith(lowerPartial)) {
        // include the full key (e.g., 'teacher')
        expandedQueries.add(lk);
        // include its synonyms (e.g., 'schools')
        synonymMap[k].forEach(syn => expandedQueries.add(syn.toLowerCase()));
      }
    });
  };

  // ✅ Handle individual words for synonyms + prefix-based expansions
  words.forEach(word => {
    expandedQueries.add(word); // already lowercase
    if (synonymMap[word]) {
      synonymMap[word].forEach(syn => expandedQueries.add(syn.toLowerCase()));
    }
    // Prefix-based expansion to handle partial inputs like 'teach' → 'teacher'/'teachers' → 'schools'
    addPrefixSynonyms(word);
  });

  return Array.from(expandedQueries);
};

// 🔹 Preprocess Search Data into a Safe Format
const preprocessSearchData = (searchData: any) => {
  if (!searchData || typeof searchData !== 'object') return []; // Prevent errors

  return Object.keys(searchData).map((key) => ({
    key,
    directive: searchData[key]?.directive || "",
    data: searchData[key]?.data || "",
  }));
};

// 🔹 Create Stadia Maps Geocoding API Instance
// Using domain-based authorization. If needed, you can pass an API key in the Configuration.
const config = new Configuration({
  // apiKey: '', // Not required if your domain is whitelisted
});
const geocodingApi = new GeocodingApi(config);

// 🔹 Stadia Geocoding Integration using the Autocomplete endpoint
// This function appends a site-configured geo context to the query and constrains
// geocoding to the site's configured map bounds.
const fetchStadiaGeocode = async (query: string): Promise<{ key: string; data: string; lat?: number; lng?: number; city?: string }[]> => {
  const geocodeContext = (siteConfig as any)?.search?.geocodeContext || 'New York City, NY';
  const appendedQuery = `${query}, ${geocodeContext}`;

  const configuredBounds = (siteConfig as any)?.search?.geocodeBounds;
  const [boundA, boundB] = siteConfig.map.bounds;
  const minLat = configuredBounds?.minLat ?? Math.min(boundA[0], boundB[0]);
  const maxLat = configuredBounds?.maxLat ?? Math.max(boundA[0], boundB[0]);
  const minLon = configuredBounds?.minLon ?? Math.min(boundA[1], boundB[1]);
  const maxLon = configuredBounds?.maxLon ?? Math.max(boundA[1], boundB[1]);

  // Build a request object that includes both the appended query and bounding box.
  // Note: We cast the object as "any" to allow extra properties since AutocompleteRequest
  // doesn't formally include "boundary".
  const request = {
    text: appendedQuery,
    boundary: {
      rect: { minLon, minLat, maxLon, maxLat }
    }
  } as any as AutocompleteRequest;

  try {
    // Make the autocomplete request via the Geocoding API instance.
    const response = await geocodingApi.autocomplete(request);
    // The response is expected to be a GeoJSON-like object with a "features" array.
    if (response && response.features && Array.isArray(response.features)) {
      const mappedResults = response.features.map((feature: any) => {
        const props = feature.properties || {};
        const geometry = feature.geometry || {};
        // Expect GeoJSON: coordinates are in [lon, lat] order.
        const coordinates = geometry.coordinates || [];
        const lng = coordinates[0] || null;
        const lat = coordinates[1] || null;
        // Extract a city/locality if available.
        const city = (props.city || props.locality || "").toLowerCase();
        return {
          key: props.label || props.name || "Unknown",
          data: props.postcode || "",
          lat,
          lng,
          city,
        };
      });
      // Filter out any results that:
      // 1. Do not have valid lat/lng,
      // 2. Fall outside the configured site bounding box.
      return mappedResults.filter(r =>
        r.lat !== null &&
        r.lng !== null &&
        r.lat >= minLat &&
        r.lat <= maxLat &&
        r.lng >= minLon &&
        r.lng <= maxLon
      );
    }
    return [];
  } catch (error) {
    console.error("Error fetching geocoding data:", error);
    return [];
  }
};

type ResultItem = { key: string; data: string; lat?: number; lng?: number };

const SearchPopup = ({ 
  isOpen, 
  onClose,
  searchDataAllPages = {}, 
  groupedModules,
  allData,
  setModuleIdIncrement,
  moduleIdIncrement,
}: { 
  isOpen: boolean;
  onClose: () => void;
  searchDataAllPages: any;
  groupedModules: any;
  allData: any;
  setModuleIdIncrement: Function;
  moduleIdIncrement: number;
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredResults, setFilteredResults] = useState<ResultItem[]>([]);
  const [outstandingSearch, setOutstandingSearch] = useState<boolean>(false);
  const [searchData, setSearchData] = useState<any>(null);
  const moduleId = useGlobalModuleId();
  const activePage = useGlobalActivePage();
  const activeGeography = useGlobalActiveGeography();
  
  useEffect(() => {
    console.log("C032825 searchDataAllPages", searchDataAllPages);
    if (searchDataAllPages && searchDataAllPages[activePage] && activePage !== "compare") {
      setSearchData(searchDataAllPages[activePage]);
    }
  }, [searchDataAllPages, activePage]);

  useEffect(() => {
    console.log("D032825 searchDataAllPages", searchDataAllPages);
    if (activePage === "compare" && allData && allData[activeGeography] && allData[activeGeography].meta) {
      console.log("D032825 allData[activeGeography].meta", allData[activeGeography].meta);  
      /*arr.forEach(item => {
        console.log("C032825 Array item:", item);
        let _item = { directive: "compare", data: item["VARIABLE_NAME"], directive_data: item["moduleId"], category: item.Sort.Category_ }
        _searchDataDB[`${item.Sort.Category_} - ${item["DISPLAY_NAME"]}`] 
            ? _searchDataDB[`${item.Sort.Category_} - ${item["DISPLAY_NAME"]}`].push(_item)
            : _searchDataDB[`${item.Sort.Category_} - ${item["DISPLAY_NAME"]}`] = [_item]
      });*/
      let options = allData[activeGeography].meta;
      let _searchDataCompare: any = { };
      Object.keys(options.DISPLAY_NAME).forEach(key => {
        if (options[activeGeography][key] == "X"){
          if (options.Comparisons[key] == "X"){
            console.log("D032825 options.DISPLAY_NAME[key]", options.DISPLAY_NAME[key]); 
            console.log("D032825 options.VARIABLE_NAME[key]", options.VARIABLE_NAME[key]); 
            console.log("D032825 options.VARIABLE_NAME[key]", options.VARIABLE_NAME[key]); 
            let _item = { directive: "compare", data: options.VARIABLE_NAME[key], directive_data: options.VARIABLE_NAME[key], category: "" }
            _searchDataCompare[`${options.DISPLAY_NAME[key]}`] 
                ? _searchDataCompare[`${options.DISPLAY_NAME[key]}`].push(_item)
                : _searchDataCompare[`${options.DISPLAY_NAME[key]}`] = [_item]
          }
        }
      });
      setSearchData(_searchDataCompare);
    }
  }, [searchDataAllPages, activePage, allData, activeGeography]);

  useEffect(() => {
    console.log("C032825 searchData", searchData);
  }, [searchData]);

  useEffect(() => {
    if (outstandingSearch) {
      setModuleIdIncrement(moduleIdIncrement + 1);
      setOutstandingSearch(false);
    }
  }, [moduleId]);

  // ✅ Prevent errors by ensuring searchData is always an object
  const safeSearchData = searchData && typeof searchData === 'object' ? searchData : {};

  // 🔹 Preparing Data for Search Index
  const searchIndex = preprocessSearchData(safeSearchData);

  // 🔹 Fuse.js Configuration
  /*const fuseOptions = {
    keys: ['key', 'data'],
    threshold: 0.3,  // Adjusted to reduce false positives
    includeScore: false,
    minMatchCharLength: 2, // Reduced from 3 to 2 for better prefix matching
    findAllMatches: true,  // ✅ Allows partial word matches
    ignoreLocation: true,  // ✅ Ensures matches can be anywhere
    isCaseSensitive: false, // ✅ Ensures synonyms & data are matched ignoring case
  };*/
  const fuseOptions = {
    keys: ['key', 'data'],
    threshold: 0.3,            // Lower threshold => stricter matching => fewer false positives.
                                  // e.g., 0.2 means the text has to be more closely matched to the query.
    minMatchCharLength: 3,     // Minimum number of characters that must match => helps reduce partial matches.
                                  // e.g., 3 means you need at least 3 matching characters for a match to count.
    isCaseSensitive: false,    //   => When false, 'ABC' will match 'abc'.
    // distance: 100,          //   => Determines how far the search term can be from the expected location in the text.
    // location: 0,            //   => The approximate position in the text where the pattern is expected to be found.
    // findAllMatches: false,  //   => When false, it stops after finding the best match (less “noisy” results). If true, it finds all matches that satisfy the threshold.
    // ignoreLocation: false,  //   => If true, the distance setting is ignored and matches can be anywhere.
    shouldSort: true,          //   => Sorts the result list by best match (lowest score).
    includeScore: false,       //   => If true, each match result includes the matching score.
    includeMatches: true,      //   => Include match metadata so we can log why it matched.
    useExtendedSearch: false,  //   => If true, allows more advanced search patterns like exact match (=) or fuzzy match (~).
    // Added options for better partial word matching
    tokenize: true,            //   => break strings into words
    matchAllTokens: false,     //   => match any word, not all words
    ignoreLocation: true,      //   => allow matches anywhere in the string
  };
  

  const fuse = new Fuse(searchIndex, fuseOptions);

  // 🔹 Handle Search Input
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawQuery = e.target.value;
    setSearchTerm(rawQuery);

    const MIN_PARTIAL_LEN = 3;
    if (rawQuery.length >= MIN_PARTIAL_LEN) {
      const expandedQueries = expandQuery(rawQuery);
      const rawTokens = rawQuery.toLowerCase().split(/\s+/).filter(Boolean);
      const multiWordKey = rawQuery.toLowerCase();
      const containsDissociateTerm = rawTokens.some((w) => !!dissociateMap[w]);

      let allResults: ResultItem[] = [];
      const originMap = new Map<string, 'raw' | 'syn' | 'both' | 'geo'>();

      // Collect Fuse results and tag origin as raw/syn/both
      expandedQueries.forEach(query => {
        const isSynonym = !rawTokens.includes(query);
        const fuseResults = fuse.search(query);
        fuseResults.forEach(r => {
          const it = r.item as ResultItem;
          const prev = originMap.get(it.key);
          const curr = isSynonym ? 'syn' : 'raw';
          const nextOrigin = prev ? (prev === curr ? prev : 'both') : curr;
          const keyLc = (it.key || '').toLowerCase();
          const dataLc = (it.data || '').toLowerCase();
          const qLc = query.toLowerCase();
          // For raw queries, require a contiguous substring of length >= MIN_PARTIAL_LEN.
          // For synonym queries, allow Fuse-detected matches even if the synonym
          // is not an exact contiguous substring (e.g., 'schools' vs 'school').
          const rawContiguous = qLc.length >= MIN_PARTIAL_LEN && (keyLc.includes(qLc) || dataLc.includes(qLc));
          const contiguousHit = isSynonym ? true : rawContiguous;
          if (contiguousHit) {
            originMap.set(it.key, nextOrigin);
          }

          const directDisplayMatch = it.key && it.key.toLowerCase() === query.toLowerCase();
          const matchedFields = Array.isArray(r.matches) ? Array.from(new Set(r.matches.map(m => String(m.key)))) : [];

          // Determine which source tokens produced this synonym, if applicable
          const synonymSources = new Set<string>();
          const allSourceTokens = [multiWordKey, ...rawTokens];
          allSourceTokens.forEach(src => {
            const syns = synonymMap[src];
            if (syns && syns.map(s => s.toLowerCase()).includes(query.toLowerCase())) {
              synonymSources.add(src);
            }
          });
          const synonymMappings = Array.from(synonymSources).map(src => ({ source: src, synonyms: synonymMap[src] }));

          console.log('010526 match', {
            query,
            matchType: isSynonym ? 'synonym' : 'raw',
            originPrev: prev || null,
            originNext: nextOrigin,
            itemKey: it.key,
            itemData: it.data,
            directDisplayMatch,
            matchedFields,
            synonymSources: Array.from(synonymSources),
            synonymMappings,
            contiguousHit,
            rawContiguous,
          });

          // Log exact character indices and matched substrings for each Fuse match
          if (Array.isArray(r.matches)) {
            const matchIndicesDetails = r.matches.map(m => {
              const fieldKey = String(m.key);
              const valueStr = typeof m.value === 'string'
                ? m.value
                : (fieldKey === 'key' ? (it.key || '') : ((it as any)[fieldKey] || ''));
              const segments = Array.isArray(m.indices)
                ? m.indices.map(([start, end]: [number, number]) => ({
                    start,
                    end,
                    text: valueStr.slice(start, end + 1),
                  }))
                : [];
              return {
                field: fieldKey,
                valuePreview: valueStr,
                indices: m.indices,
                segments,
              };
            });
            console.log('010526 matchIndices', {
              query,
              itemKey: it.key,
              details: matchIndicesDetails,
            });
          }

          if (contiguousHit) {
            allResults.push(it);
          } else {
            console.log('010526 filteredOutNonContiguous', {
              query,
              itemKey: it.key,
              itemData: it.data,
              reason: 'raw fuzzy match without contiguous substring (e.g., "teach" matched "each"). Synonym results are allowed.',
            });
          }
        });
      });

      // Deduplicate Fuse results
      const uniqueResults = Array.from(new Map(allResults.map(item => [item.key, item])).values());

      // Geocode results (tag origin as geo if not previously set)
      (async () => {
        const geoResults = await fetchStadiaGeocode(rawQuery);
        geoResults.forEach(it => {
          console.log('010526 match', {
            query: rawQuery,
            matchType: 'geocode',
            itemKey: it.key,
            itemData: it.data,
            lat: it.lat,
            lng: it.lng,
            city: (it as any).city,
          });
          if (!originMap.has(it.key)) originMap.set(it.key, 'geo');
        });

        const combinedResults = [ ...uniqueResults, ...geoResults ];
        const finalResults = Array.from(new Map(combinedResults.map(item => [item.key, item])).values());

        // Enforce required only for raw-origin items when dissociation term is present
        finalResults.forEach(it => {
          console.log('010526 finalCandidate', {
            itemKey: it.key,
            origin: originMap.get(it.key) || 'raw',
          });
        });
        const filtered = applyDissociation(rawQuery, finalResults, containsDissociateTerm, originMap);
        console.log('010526 dissociation', {
          rawQuery,
          containsDissociateTerm,
          beforeCount: finalResults.length,
          afterCount: filtered.length,
        });

        setFilteredResults(filtered);
      })();
    } else {
      setFilteredResults([]);
      if (rawQuery.length > 0 && rawQuery.length < MIN_PARTIAL_LEN) {
        console.log('010526 filteredOutShortQuery', {
          rawQuery,
          reason: `query length < ${MIN_PARTIAL_LEN} (requires contiguous substring of length >= ${MIN_PARTIAL_LEN})`,
        });
      }
    }
  };

  // 🔹 Handle Selection
  /*const handleSelect = (itemKey: string) => {
    console.log(`✅ Selected: ${itemKey}`);
    setOutstandingSearch(true);
    onSetModuleId(safeSearchData[itemKey]?.directive || "");
    onSetActiveIndicator(safeSearchData[itemKey]?.data || "");
    onClose();
  };*/

  const handleSelect = (result: ResultItem) => {
    console.log(`B121425 You selected: ${result.key}`);
    console.log("B121425 Full result", result);
    console.log("B121425 searchTerm", searchTerm);
    if (result.lat && result.lng) { // If the result has coordinates, use them
      console.log(`A032825 Coordinates: lat=${result.lat}, lng=${result.lng}`);
      onSetSearchLatLng({"lat": result.lat, "lng": result.lng, "label": result.key});
    } else {
      // If this result matches one of your existing searchData items, use that directive.
      if (searchData[result.key] && searchData[result.key][0]) {
        console.log("A032825 searchData[result.key]", searchData[result.key]);
        console.log("A032825 searchData", searchData);
        if (activePage === "explorer") {
          console.log("F050625 searchData[result.key]", searchData[result.key]);
          console.log("F050625 activeGeography", activeGeography);
          checkGeoAvailability(searchData[result.key][0], activeGeography, ["CD","PUMA","NTA", "Tract", "Boro"]);
          console.log("X122725 searchData[result.key][0].directive_data", searchData[result.key][0].directive_data);
          onSetModuleId(searchData[result.key][0].directive_data);
          onSetActiveModuleId(searchData[result.key][0].directive_data);
          onSetActiveIndicator(searchData[result.key][0].data, "search", "explorer");
          setModuleIdIncrement(moduleIdIncrement + 1);
        } else if (activePage === "stories") {
          // TBD
          console.log("X122725 searchData[result.key][0].directive_data", searchData[result.key][0].directive_data);
          onSetSTModuleId(searchData[result.key][0].directive_data);
          onSetActiveSTModuleId(searchData[result.key][0].directive_data);
          onSetActiveIndicator(searchData[result.key][0].data, "search", "explorer");
          setModuleIdIncrement(moduleIdIncrement + 1);
        } else if (activePage === "dashboard") {
          console.log("A032825 DASHBOARD SEARCH HAPPENED searchData[result.key]", searchData[result.key]);
          onSetDBModuleId(searchData[result.key][0].directive_data);
          onSetActiveDBModuleId(searchData[result.key][0].directive_data);
          onSetActiveIndicator(searchData[result.key][0].data, "search", "dashboard");
          setModuleIdIncrement(moduleIdIncrement + 1);
        } else if (activePage === "compare") {
          console.log("A032825 DASHBOARD SEARCH HAPPENED searchData[result.key]", searchData[result.key]);
          onSetActiveIndicator(searchData[result.key][0].data, "search", "compare");
        } else if (activePage === "rg") {
          console.log("A032825 RACE GENDER SEARCH HAPPENED searchData[result.key]", searchData[result.key]);
          onSetRGModuleId(searchData[result.key][0].directive_data);
          onSetActiveRGModuleId(searchData[result.key][0].directive_data);
          onSetActiveIndicator(searchData[result.key][0].data, "search", "rg");
          setModuleIdIncrement(moduleIdIncrement + 1);
        }
      }
      // Logic to handle specific selection can go here
      // Example: navigate to a specific page or filter data based on selection
    }
    if (result) {  
      let phParams = {
        event_class: 'search_result_selected',
        view: activePage,
        search_term: searchTerm,
        result_key: result.key,
        result_type: result.lat && result.lng ? 'geocode' : 'indicator_lookup',
      }
      posthog.capture('ui_event', phParams);
      console.log("A121225 PostHog event: ui_event", phParams);
    }
    setOutstandingSearch(true);
    onClose(); // Close the popup after selection
  };

  if (!isOpen) return null;

  return (
    <div style={{
        position: 'fixed',
        top: '50px',
        left: 250,
        width: '100vw',
        height: 'calc(100% - 50px)',
        backgroundColor: '#fff',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1001,
      }}>
      <div
        style={{
          position: 'absolute',
          top: '20px',
          right: '20px',
          cursor: 'pointer',
          fontSize: '24px',
        }}
        onClick={() => {
          onClose();
          const phParams = {
            event_class: 'search_dismissed',
            view: activePage,
            search_term: searchTerm,
            result_key: 'search_session_closed',
            result_type: 'dismiss',
          };
          posthog.capture('ui_event', phParams);
          console.log('A121225 PostHog event: ui_event', phParams);
        }}
      >
        X
      </div>
      <div style={{ textAlign: 'left', width: '80%', maxWidth: '600px' }}>
        <div style={{ position: 'relative', width: '100%', marginBottom: '20px' }}>
          <input
            type="text"
            value={searchTerm}
            onChange={handleInputChange}
            placeholder="Search..."
            style={{
              width: '100%',
              padding: '10px',
              fontSize: '2rem',
              border: 'none',
              borderBottom: '2px solid #333',
              outline: 'none',
              textAlign: 'left',
              color: '#666',
            }}
          />
          <div
            style={{
              position: 'absolute',
              right: '10px',
              top: '50%',
              transform: 'translateY(-50%)',
              cursor: 'pointer',
            }}
          >
            <svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="18.3337" cy="11.6667" r="9.91667" fill="none" stroke="black" strokeWidth="3.5" />
              <path d="M2 28.0001L11.3333 18.6667" stroke="black" strokeWidth="3.5" strokeLinecap="round" />
            </svg>
          </div>
        </div>

        {/* 🔹 Typeahead Suggestions */}
        <ul style={{ listStyleType: 'none', padding: 0, margin: 0, width: '100%', maxHeight: '200px', overflowY: 'auto', textAlign: 'left' }}>
          {filteredResults.length > 0 ? (
            filteredResults.map(result => (
              <li
                key={result.key}
                style={{ padding: '10px', cursor: 'pointer', backgroundColor: '#f5f5f5', marginBottom: '5px' }}
                onClick={() => handleSelect(result)}
              >
                {result.key}
              </li>
            ))
          ) : (
            <li style={{ padding: '10px', fontStyle: 'italic' }}>No results found</li>
          )}
        </ul>

        {/* 🔹 Restored Instructions Below Input Box */}
        <div style={{ marginTop: '30px', fontSize: '1.2rem' }}>
          <p>Type anything you want into this search bar - including areas, zip codes, population groups, statistics, or even combinations of statistics you’d like to see.</p>
          <p><b>Examples:</b></p>
          <ul style={{ listStyleType: 'none' }}>
            <li>Total Population</li>
            <li>Families</li>
            {/*<li>Alzheimer's Disease</li>
            <li>Gender Statistics</li>*/}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default SearchPopup;
